import React, { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, ChevronLeft, ChevronRight, ChevronUp, PanelRight, Share2, X } from 'lucide-react';
import type { DetrixSession } from '../types';
import { useSessionUiStore } from '../store/useSessionUiStore';
import { Toolbar } from '../components/Toolbar';
import { PlaybackClock } from '../components/PlaybackClock';
import { SessionTimeline } from '../features/timeline/SessionTimeline';
import { TabBar } from '../components/TabBar';
import { Inspector } from '../features/inspector/Inspector';
import { SessionSharePanel } from '../features/sharing/SessionSharePanel';
import { SessionWorkspaceSplit } from '../components/SessionWorkspaceSplit';
import { StepsPanel } from '../features/panels/StepsPanel';
import { ActionsPanel } from '../features/panels/ActionsPanel';
import { NetworkPanel } from '../features/panels/NetworkPanel';
import { EventsPanel } from '../features/panels/EventsPanel';
import { selectionSummary } from '../utils/selectionSummary';

export function SessionViewerApp({ session }: { session: DetrixSession }) {
  const activeTab = useSessionUiStore((s) => s.activeTab);
  const selectedId = useSessionUiStore((s) => s.selectedId);
  const [isMobile, setIsMobile] = useState(false);
  /**
   * Small screens: Sharing stays docked at the bottom on every tab; inspector opens as a layer over the list only.
   * Re-open via FAB when dismissed — no desktop-style split gutters.
   */
  const [mobileInspectorOpen, setMobileInspectorOpen] = useState(true);
  const [mobileShareDockOpen, setMobileShareDockOpen] = useState(true);
  const [desktopInspectorCollapsed, setDesktopInspectorCollapsed] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 1024);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (!isMobile) setMobileShareDockOpen(true);
  }, [isMobile]);

  useEffect(() => {
    if (selectedId) setMobileInspectorOpen(true);
  }, [selectedId]);

  useEffect(() => {
    if (!isMobile || !selectedId || !mobileInspectorOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileInspectorOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isMobile, selectedId, mobileInspectorOpen]);

  const inspectPreview = useMemo(
    () => selectionSummary(session, activeTab, selectedId),
    [session, activeTab, selectedId],
  );

  const renderPanel = () => {
    switch (activeTab) {
      case 'steps':
        return <StepsPanel session={session} />;
      case 'actions':
        return <ActionsPanel session={session} />;
      case 'network':
        return <NetworkPanel session={session} />;
      case 'events':
        return <EventsPanel session={session} />;
      default:
        return null;
    }
  };

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-[var(--bg-editor)] text-[var(--text-primary)]">
      <PlaybackClock />
      <Toolbar session={session} />

      <div className="flex min-h-0 flex-1 flex-col">
        <div className="flex min-h-0 min-w-0 max-h-[min(36rem,50vh)] shrink-0 flex-col overflow-x-hidden overflow-y-auto border-b border-[var(--border-subtle)]">
          <div className="flex min-h-0 min-w-0 shrink-0 flex-col">
            <SessionTimeline session={session} />
          </div>
        </div>

        <div className="relative z-0 flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          <TabBar session={session} />

          <main className="relative z-0 flex min-h-0 flex-1 flex-col overflow-hidden">
            {isMobile ? (
              <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
                  <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">{renderPanel()}</div>

                  {selectedId && mobileInspectorOpen ? (
                    <div
                      role="dialog"
                      aria-modal
                      aria-label="Inspection details"
                      className="absolute inset-0 z-40 flex min-h-0 flex-col overscroll-contain bg-[var(--bg-editor)] shadow-[var(--mobile-sheet-shadow)]"
                    >
                      <div className="flex shrink-0 items-center gap-2 border-b border-[var(--border-subtle)] bg-[var(--bg-sidebar)] px-2 py-2 pt-[max(0.5rem,env(safe-area-inset-top,0px))]">
                        <button
                          type="button"
                          onClick={() => setMobileInspectorOpen(false)}
                          className="flex shrink-0 items-center gap-1.5 rounded-md px-2 py-1.5 text-[13px] font-medium text-[var(--accent)] transition-colors hover:bg-white/[0.05]"
                        >
                          <ArrowLeft className="h-4 w-4 shrink-0" aria-hidden />
                          List
                        </button>
                        <p className="min-w-0 flex-1 truncate text-center text-[11px] text-[var(--text-muted)]" title={inspectPreview}>
                          {inspectPreview}
                        </p>
                        <button
                          type="button"
                          aria-label="Clear selection"
                          title="Clear selection"
                          onClick={() => useSessionUiStore.getState().setSelectedId(null)}
                          className="flex shrink-0 items-center justify-center rounded-md px-2 py-2 text-[var(--text-muted)] transition-colors hover:bg-white/[0.04] hover:text-[var(--text-primary)]"
                        >
                          <X className="h-3.5 w-3.5 shrink-0" aria-hidden />
                        </button>
                      </div>
                      <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-[var(--bg-panel)]">
                        <div className="min-h-0 flex-1 overflow-hidden">
                          <Inspector session={session} />
                        </div>
                      </div>
                    </div>
                  ) : null}

                  {selectedId && !mobileInspectorOpen ? (
                    <button
                      type="button"
                      aria-label="Open inspection details"
                      title={inspectPreview}
                      onClick={() => setMobileInspectorOpen(true)}
                      className="absolute bottom-4 right-4 z-30 flex h-12 w-12 items-center justify-center rounded-full border border-[var(--accent)]/40 bg-[var(--accent)] text-[var(--on-accent)] shadow-lg transition-transform hover:scale-105 active:scale-95"
                    >
                      <PanelRight className="h-5 w-5 shrink-0" aria-hidden />
                    </button>
                  ) : null}
                </div>

                {mobileShareDockOpen ? (
                  <div
                    className="max-h-[min(420px,48vh)] min-h-0 shrink-0 overflow-y-auto overflow-x-hidden border-t border-[var(--border-subtle)] bg-[var(--bg-panel)] pb-[max(0.25rem,env(safe-area-inset-bottom,0px))]"
                    aria-label="Sharing"
                  >
                    <SessionSharePanel
                      compact
                      mobileDock
                      onRequestCollapse={() => setMobileShareDockOpen(false)}
                    />
                  </div>
                ) : (
                  <div className="flex shrink-0 items-center justify-end border-t border-[var(--border-subtle)] bg-[var(--bg-panel)] px-2 py-1 pb-[max(0.25rem,env(safe-area-inset-bottom,0px))]">
                    <button
                      type="button"
                      className="flex items-center gap-1.5 rounded-md py-1 pl-2 pr-1.5 text-[var(--text-muted)] transition-colors hover:bg-[var(--bg-elevated)]/60 hover:text-[var(--text-primary)]"
                      aria-expanded={false}
                      aria-label="Show sharing"
                      title="Show sharing"
                      onClick={() => setMobileShareDockOpen(true)}
                    >
                      <Share2 className="h-3.5 w-3.5 shrink-0" aria-hidden />
                      <span className="text-[10px] font-bold uppercase tracking-wide text-[var(--text-secondary)]">Sharing</span>
                      <ChevronUp className="h-4 w-4 shrink-0 opacity-80" aria-hidden />
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex min-h-0 min-w-0 w-full flex-1 flex-row">
                {desktopInspectorCollapsed ? (
                  <>
                    <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">{renderPanel()}</div>
                    <button
                      type="button"
                      aria-label="Show list, details, and sharing"
                      title="Show list, details, and sharing"
                      aria-expanded={false}
                      onClick={() => setDesktopInspectorCollapsed(false)}
                      className="hidden min-h-0 w-8 shrink-0 flex-col items-center justify-center border-l border-[var(--border-subtle)] bg-[var(--bg-sidebar)] py-2 text-[var(--text-muted)] transition-colors hover:bg-[var(--bg-elevated)] hover:text-[var(--text-primary)] lg:flex"
                    >
                      <ChevronLeft className="h-4 w-4 shrink-0" aria-hidden />
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      aria-label="Hide details and sharing columns"
                      title="Hide details and sharing columns"
                      aria-expanded
                      onClick={() => setDesktopInspectorCollapsed(true)}
                      className="hidden min-h-0 w-8 shrink-0 flex-col items-center justify-center border-r border-[var(--border-subtle)] bg-[var(--bg-sidebar)] py-2 text-[var(--text-muted)] transition-colors hover:bg-[var(--bg-elevated)] hover:text-[var(--text-primary)] lg:flex"
                    >
                      <ChevronRight className="h-4 w-4 shrink-0" aria-hidden />
                    </button>
                    <SessionWorkspaceSplit
                      list={<div className="flex h-full min-h-0 min-w-0 flex-col overflow-hidden">{renderPanel()}</div>}
                      inspector={<Inspector session={session} />}
                      share={<SessionSharePanel />}
                    />
                  </>
                )}
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
